
/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Resource utils
 *
 * @file ResourceUtils.js
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.0
 */

import {
  BOOK_LEVEL_RESOURCE,
  BULK_DOWNLOAD_STATUS_COMPLETED,
  BULK_DOWNLOAD_STATUS_NOT_STARTED,
  CONTENT_TITLE_OTHER,
  CONTENT_TITLE_OTHER_ICON,
  CONTENT_TYPE_DATA_CONNECTION,
  CONTENT_TYPE_KEYS,
  PAGE_LEVEL_RESOURCE,
  THEME_DARK,
  THEME_SEPIA,
  USER_ROLE_STUDENT
} from '../common/constants';

class ResourceUtils {
  /**
   * Constructs flat data
   *
   * @param {*} resourceData
   * @returns
   */
  static constructFlatData(resourceData) {
    const resourceFlatList = [];
    const drillToDeep = (resource) => {
      const children = resource.subtopic;

      if (children) {
        children.forEach((child) => {
          drillToDeep(child);
        });
      } else {
        const currentResource = { ...resource };
        resourceFlatList.push(currentResource);
      }
    };

    resourceData.forEach((node) => {
      drillToDeep(node);
    });

    return resourceFlatList;
  }

  /**
   * Construct the flat structured resource
   *
   * @param {*} resourceData
   */
  static constructFlatResource(resourceData) {
    const resourceFlatList = [];
    const drillToDeep = (resource, role, name, parentIndex) => {
      const children = resource.subtopic;

      if (children) {
        children.forEach((child, index) => {
          drillToDeep(child, role, `${name} > ${child.name}`, `${parentIndex}_${index}`);
        });
      } else {
        const currentResource = { ...resource, limitByRoles: role, path: name, id: parentIndex };
        resourceFlatList.push(currentResource);
      }
    };

    resourceData.forEach((node, index) => {
      drillToDeep(node, node.limitByRoles, node.name, index);
    });

    return resourceFlatList;
  }

  /**
   * Add some required property to the structured datat
   * @param {*} resourceData
   * @returns
   */
  static constructStructuredResource(resourceData, name, parentIndex) {
    let resources = resourceData;

    if (resources instanceof Array) {
      const resourceVal = resources.map((item, index) => this.constructStructuredResource(item, `${item.name}`, index));

      resources = [...resourceVal];
    } else if (resources.subtopic && resources.subtopic.length > 0) {
      const resourceVal = resources.subtopic.map((item, index) => this.constructStructuredResource(item, `${name} > ${item.name}`, `${parentIndex}_${index}`));

      resources = {
        ...resources, subtopic: [...resourceVal], id: parentIndex.toString()
      };
    } else {
      resources = {
        ...resources, path: name, id: parentIndex.toString(), status: resources.status
      };
    }

    return resources;
  }

  /**
   * Get available content type
   *
   * @param {*} resourceFlatList
   * @returns
   */
  static getAvailableContentData(resourceFlatList) {
    const contentTypesDataConnection = CONTENT_TYPE_DATA_CONNECTION;
    const availableContentData = [];
    const allContentType = [];

    contentTypesDataConnection.map(contentType => {
      const found = resourceFlatList.some(
        resource => resource.contentTypeValue === contentType[CONTENT_TYPE_KEYS.TYPE]);

      if (found) {
        availableContentData.push(contentType);
      }
      allContentType.push(contentType[CONTENT_TYPE_KEYS.TYPE]);
    });

    const OtherResourceTypeFound = resourceFlatList.some(
      resource => !allContentType.includes(resource.contentTypeValue));

    if (OtherResourceTypeFound) {
      availableContentData.push({
        icon: CONTENT_TITLE_OTHER_ICON,
        label: CONTENT_TITLE_OTHER,
        type: CONTENT_TITLE_OTHER
      });
    }

    availableContentData.sort((a, b) => {
      if (a[CONTENT_TYPE_KEYS.LABEL] < b[CONTENT_TYPE_KEYS.LABEL]) {
        return -1;
      }
      if (a[CONTENT_TYPE_KEYS.LABEL] > b[CONTENT_TYPE_KEYS.LABEL]) {
        return 1;
      }

      return 0;
    });

    return availableContentData;
  }

  /**
   * Get the empty screen description
   *
   * @param {*} isSearchAndFilterOff
   * @param {*} isBookLevelTabSelected
   * @returns
   */
  static getEmptyScreenDescription(isSearchAndFilterOff, isBookLevelTabSelected) {
    let description = 'searchEmpty';

    if (isSearchAndFilterOff && isBookLevelTabSelected) {
      description = 'bookTabEmpty';
    } else if (isSearchAndFilterOff && !isBookLevelTabSelected) {
      description = 'pageTabEmpty';
    }

    return description;
  }

  /**
   * Get label based on content type
   *
   * @param {string} contentType
   */
  static getContentTypeDetails(value, valueKey) {
    const contentTypesDataConnection = CONTENT_TYPE_DATA_CONNECTION;

    let requestedContentTypeData = [];
    requestedContentTypeData = contentTypesDataConnection.filter(contentType => (contentType[valueKey] === value));


    return requestedContentTypeData;
  }

  /**
   * Gets the resource data that will be displayed
   *
   * @param {*} resourceData
   * @param {*} role
   * @param {*} resourcelevel
   * @param {*} filteredContentType
   * @param {*} isCardView
   * @returns
   */
  static getResourceData(resourceData, role, resourcelevel, filteredContentType, isCardView, searchedKeyword) {
    let filteredResourceData = [];

    if (resourcelevel === BOOK_LEVEL_RESOURCE) {
      filteredResourceData = resourceData.filter((resource) => {
        if (role) {
          const rolesForResource = resource.limitByRoles;
          if (rolesForResource && rolesForResource.length) {
            for (let i = 0; i < rolesForResource.length; i += 1) {
              if (rolesForResource[i].toLowerCase().indexOf(role.toLowerCase()) !== -1) {
                return resource;
              }
            }
          }
        } else {
          return resource;
        }
      });
    } else if (resourcelevel === PAGE_LEVEL_RESOURCE) {
      filteredResourceData = ResourceUtils.groupResourcesBasedOnPageId(resourceData);
    }

    const allContentType = CONTENT_TYPE_DATA_CONNECTION.map(
      contentTypeData => contentTypeData[CONTENT_TYPE_KEYS.TYPE]);

    // Filter the data based on selected content Type
    if (filteredContentType.length > 0 || searchedKeyword !== '') {
      filteredResourceData = filteredResourceData.filter((resource) => {
        let tempContentType = resource.contentTypeValue;
        const found = allContentType.includes(tempContentType);
        let resourceName = resource.name || '';
        resourceName = resourceName.toLowerCase();

        if (!found) {
          tempContentType = CONTENT_TITLE_OTHER;
        }

        if (
          (filteredContentType.length > 0 && searchedKeyword === '' && filteredContentType.includes(tempContentType)) ||

            (filteredContentType.length === 0 && searchedKeyword !== ''
              && resourceName.indexOf(searchedKeyword.toLowerCase()) > -1) ||

            (filteredContentType.length > 0 && searchedKeyword !== ''
              && filteredContentType.includes(tempContentType) && resourceName.includes(searchedKeyword.toLowerCase()))
        ) {
          return resource;
        }
      });
    }

    // For card view, categorize based on resource type
    if (isCardView && resourcelevel === BOOK_LEVEL_RESOURCE
      && filteredContentType.length === 0 && searchedKeyword === '') {
      filteredResourceData = ResourceUtils.groupResourcesBasedOnContentType(filteredResourceData, allContentType);
      filteredResourceData = Object.keys(filteredResourceData).sort().reduce(
        (obj, key) => {
          const sortedResource = { ...obj };
          sortedResource[key] = filteredResourceData[key];

          return sortedResource;
        }, {});
    }

    return filteredResourceData;
  }

  /**
   * Group resource based on category
   * @param {*} resources
   * @param {*} allContentType
   * @returns
   */
  static groupResourcesBasedOnContentType(resources, allContentType) {
    const groupedResources = resources.reduce((accumulator, currentValue) => {
      let contentLabel = currentValue.contentTypeValue;

      if (allContentType.indexOf(contentLabel) < 0) {
        contentLabel = CONTENT_TITLE_OTHER;
      } else {
        const contentTypeLabel = CONTENT_TYPE_DATA_CONNECTION.filter(
          contentTypeData => currentValue.contentTypeValue === contentTypeData[CONTENT_TYPE_KEYS.TYPE]);

        contentLabel = contentTypeLabel && contentTypeLabel[0] ?
          contentTypeLabel[0][CONTENT_TYPE_KEYS.LABEL] : CONTENT_TITLE_OTHER;
      }

      accumulator[contentLabel] = [...accumulator[contentLabel] || [], currentValue];

      return accumulator;
    }, []);

    return groupedResources;
  }

  /**
   * Group resource based on category
   * @param {*} resources
   * @param {*} allContentType
   * @returns
   */
  static groupResourcesBasedOnPageId(resources) {
    let groupedResources = resources.reduce((accumulator, currentValue) => {
      const pageId = currentValue.pageId;
      if (pageId) {
        accumulator[pageId] = [...accumulator[pageId] || [], currentValue];
      }

      return accumulator;
    }, []);

    groupedResources = Object.values(groupedResources);
    groupedResources = groupedResources.reduce((r, a) => r.concat(a), []);

    // Sort the resource list in ASC by pageId
    const sortedResourcesList = groupedResources.sort((x, y) => (x.pageId.localeCompare(y.pageId, undefined, {
      numeric:
        true
    })));

    return sortedResourcesList;
  }

  /**
   * check for page resources
   * @param {*} resources
   * @returns
   */
  static checkPageResource(resources) {
    for (let i = 0; i < resources.length; i += 1) {
      if (resources[i].pageId) {
        return true;
      }
    }

    return false;
  }

  /**
   * Returns the background and color for each resouce thumbnails
   *
   * @param {*} theme
   * @returns
   */
  static getThumbnailColorPalette = (theme) => {
    let backgroundColor = {
      blueMedium: '#E5F2F5',
      purpleMedium: '#EFECF9',
      pinkPrimary: '#FCE6F2',
      yellowPrimary: '#FDF6EA',
      tealMedium: '#E5F2F0',
      medGrey: '#F5F6F7'
    };
    let color = {
      blueMedium: '#005F79',
      purpleMedium: '#4F2CB0',
      pinkPrimary: '#B00A4B',
      yellowPrimary: '#9E4200',
      tealMedium: '#00655B',
      medGrey: '#4F5561'
    };

    switch (theme) {
      case THEME_DARK:
        backgroundColor = {
          blueMedium: '#1E2E34',
          purpleMedium: '#2B2834',
          pinkPrimary: '#332A30',
          yellowPrimary: '#322F27',
          tealMedium: '#273233',
          medGrey: '#303134'
        };
        color = {
          blueMedium: '#31D0FD',
          purpleMedium: '#AE90FF',
          pinkPrimary: '#E5067C',
          yellowPrimary: '#EEA42E',
          tealMedium: '#01CBB7',
          medGrey: '#F7F9FD'
        };
        break;
      case THEME_SEPIA:
        backgroundColor = {
          blueMedium: '#E3EDE5',
          purpleMedium: '#EDE7E9',
          pinkPrimary: '#FAE2E2',
          yellowPrimary: '#FBF1DA',
          tealMedium: '#E3EDE0',
          medGrey: '#EBEADF'
        };
        break;
      default:
        break;
    }

    return { backgroundColor, color };
  };

  /**
   * Hadles the scroll to current page resource
   *
   * @param {*} currentPageId
   */
  static handleScroll(currentPageId) {
    const scrollContainer = document.getElementById('vega-reader-drawer-scroll');

    if (scrollContainer) {
      const scrollbody = scrollContainer.firstChild;

      if (currentPageId && currentPageId.length > 0) {
        const firstPageID = currentPageId[0];

        const currentPageElements = document.getElementsByClassName(`pageId-${firstPageID}`);
        const firstCurrentPageElement = currentPageElements && currentPageElements[0];

        if (firstCurrentPageElement && firstCurrentPageElement.offsetTop !== scrollbody.scrollTop) {
          scrollbody.scrollTop = firstCurrentPageElement.offsetTop;
        }
      }
    }
  }

  /**
   * Hadles the scroll to tab change
   *
   */
  static handleScrollOnTabChange() {
    const scrollContainer = document.getElementById('vega-reader-drawer-scroll');

    if (scrollContainer) {
      const scrollbody = scrollContainer.firstChild;

      scrollbody.scrollTop = 0;
    }
  }

  /**
   * Returns whether the parent resource is downloadable or not
   *
   * @param {*} resourceItem
   * @returns
   */
  static isParentResourceDownloadable(resourceItem) {
    let isParentDownloadable = false;
    const drillToDeep = (resource) => {
      const children = resource.subtopic;

      if (children && !isParentDownloadable) {
        children.forEach((child) => {
          drillToDeep(child);
        });
      } else if (ResourceUtils.isResourceDownloadable(resource)) {
        isParentDownloadable = true;
      }
    };

    resourceItem.forEach((node) => {
      drillToDeep(node);
    });

    return isParentDownloadable;
  }

  /**
   * Returns whether is bulk download status is in progress or not
   *
   * @param {*} status
   * @returns
   */
  static isBulkDownloadInProgress(status) {
    if (status === BULK_DOWNLOAD_STATUS_COMPLETED || status === BULK_DOWNLOAD_STATUS_NOT_STARTED) {
      return false;
    }

    return true;
  }

  /**
    * Returns whether the resource is downloadable or not
    *
    * @param {*} resourceItem
    * @returns
    */
  static isResourceDownloadable(resourceItem) {
    return resourceItem && resourceItem.isUserDownloadable && !resourceItem.isDownloadable;
  }

  static isStudentRoleTab(showTeacherTab, showStudentTab, userRole) {
    if (userRole !== USER_ROLE_STUDENT && !showTeacherTab && showStudentTab) {
      return true;
    }

    return false;
  }
}

export default ResourceUtils;
